import axios from 'axios';
let API_URL;
if (process.env.REACT_APP_DEV==='dev') {
  API_URL = process.env.REACT_APP_API_URL;
} else {
  API_URL = process.env.REACT_APP_API_BACKEND_URL;
}

// Token alımı için güvenli ve debug edilebilir bir yardımcı fonksiyon
export const getTokenFromStorage = () => {
  try {
    const token = localStorage.getItem('token');
    // console.log("getTokenFromStorage called, token:", token ? "exists" : "not found");
    return token;
  } catch (error) {
    // console.error("Error retrieving token from localStorage:", error);
    return null;
  }
};

// Axios interceptor'ü için token ayarı
const setAuthToken = (token) => {
  try {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  } catch (error) {
    // console.error("Error setting auth token:", error);
  }
};

// Axios response interceptor ekleme
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Orijinal isteği sakla
    const originalRequest = error.config;
    
    // 401 Unauthorized hatası ve retry flag false ise
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Token yenileme isteği yap
        const token = localStorage.getItem('token');
        
        if (token) {
          // Token'ı doğrudan kullan (geçerli ise)
          setAuthToken(token);
          // Orijinal isteği yeniden dene
          return axios(originalRequest);
        }
      } catch (refreshError) {
        // Token yenilenemedi, kullanıcıyı çıkış yap
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setAuthToken(null);
      }
    }
    
    // Diğer hata durumlarını rethrow
    return Promise.reject(error);
  }
);

/**
 * Kullanıcı girişi yapar
 * @param {string} username - Kullanıcı adı
 * @param {string} password - Şifre
 * @returns {Promise<Object>} Kullanıcı ve token bilgileri
 */
export const login = async (username, password) => {
  try {
    const response = await axios.post(`${API_URL}/auth/login`, { username, password });
    
    if (response.data.success && response.data.token) {
      // Token'ı localStorage'a kaydet
      localStorage.setItem('token', response.data.token);
      // console.log("Token saved to localStorage during login");
      
      // Token'ı axios headers'a ekle
      setAuthToken(response.data.token);
      
      // API'den kullanıcı bilgilerini al
      try {
        const userData = await getMe();
        
        // User bilgisini localStorage'a kaydet
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Kullanıcı bilgilerini yanıta ekle
        return {
          success: true,
          token: response.data.token,
          user: userData
        };
      } catch (userError) {
        // console.error('User data fetch error:', userError);
        throw userError;
      }
    }
    
    return response.data;
  } catch (error) {
    // console.error('Login error:', error);
    throw error;
  }
};

/**
 * Mevcut kullanıcı bilgilerini getirir
 * @returns {Promise<Object>} Kullanıcı bilgileri
 */
export const getMe = async () => {
  try {
    // Token'ı authorize et
    const token = getTokenFromStorage();
    if (!token) {
      // console.error('No token found in localStorage');
      throw new Error('No token found');
    }
    
    // Token'ı ayarla
    setAuthToken(token);
    
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      return response.data.data;
    } catch (apiError) {
      // console.error("API error details:", apiError.response?.data || apiError.message);
      
      // Ağ hatası veya API'ye erişilememe durumu (CORS, network error, timeout)
      if (apiError.code === 'ECONNABORTED' || !apiError.response || apiError.message.includes('Network Error')) {
        // console.warn('Network error or API unavailable. Using stored user data.');
        const storedUser = getCurrentUser();
        if (storedUser) {
          return storedUser;
        }
      }
      
      // 401 Unauthorized durumunda token'ı temizle
      if (apiError.response?.status === 401) {
        // console.warn('Token invalid/expired. Removing from localStorage.');
        localStorage.removeItem('token');
        setAuthToken(null);
      }
      
      // API hatası durumunda, localStorage'daki kullanıcı bilgilerini dön
      // Bu şekilde API geçici olarak ulaşılamaz olsa bile kullanıcı oturumu devam eder
      const storedUser = getCurrentUser();
      if (storedUser) {
        // console.warn('Could not fetch fresh user data, using stored data.');
        return storedUser;
      }
      
      throw apiError;
    }
  } catch (error) {
    // console.error('Get user error:', error);
    throw error;
  }
};

/**
 * Change password for the logged-in user
 */
export const changePassword = async (currentPassword, newPassword) => {
  const response = await axios.put(`${API_URL}/auth/password`, {
    currentPassword,
    newPassword,
  });
  return response.data;
};

/**
 * Kullanıcıyı çıkış yapar
 * @returns {Promise<boolean>} İşlem başarılı mı?
 */
export const logout = async () => {
  try {
    await axios.get(`${API_URL}/auth/logout`);
    
    // Token ve kullanıcı bilgilerini localStorage'dan sil
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // console.log("Token removed from localStorage during logout");
    
    // Axios headers'dan authorization bilgisini kaldır
    setAuthToken(null);
    
    return true;
  } catch (error) {
    // console.error('Logout error:', error);
    
    // Hata durumunda bile yerel depolamayı temizle
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setAuthToken(null);
    
    throw error;
  }
};

/**
 * Kullanıcının giriş yapmış olup olmadığını kontrol eder
 * @returns {boolean} Kullanıcı giriş yapmış mı?
 */
export const isAuthenticated = () => {
  try {
    const hasToken = getTokenFromStorage() !== null;
    // console.log("isAuthenticated check:", hasToken);
    return hasToken;
  } catch (error) {
    // console.error("Error checking authentication status:", error);
    return false;
  }
};

/**
 * Mevcut kullanıcıyı getirir (yerel depolamadan)
 * @returns {Object|null} Kullanıcı bilgileri veya null
 */
export const getCurrentUser = () => {
  try {
    const userJson = localStorage.getItem('user');
    if (!userJson) {
      return null;
    }
    const parsedUser = JSON.parse(userJson);
    return parsedUser;
  } catch (error) {
    // console.error("Error getting current user from localStorage:", error);
    // Hatalı JSON veya localStorage hatası durumunda
    return null;
  }
};

// Sayfa yüklendiğinde token varsa axios headers'a ekle
const token = getTokenFromStorage();
// console.log("Initial token check on authService load:", token ? "found" : "not found");
if (token) {
  setAuthToken(token);
} 